<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import eventService from '../services/event.service'
import categoryService from '../services/category.service'
import coachService from '../services/coach.service'
import playerService from '../services/player.service'
import ParticipantSelectorV2 from '../components/ParticipantSelectorV2.vue'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Form data
const title = ref('')
const eventType = ref('')
const categoryType = ref('')
const place = ref('')
const startDate = ref('')
const startTime = ref('')
const endDate = ref('')
const endTime = ref('')
const cyclePeriod = ref('')
const cycleEndDate = ref('')

// Participant selection
const selectedPlayers = ref<string[]>([])
const selectedCoaches = ref<string[]>([])

// Data from API
const categories = ref<any[]>([])
const coaches = ref<any[]>([])
const players = ref<any[]>([])

// Loading states
const loadingCategories = ref(false)
const loadingCoaches = ref(false)
const loadingPlayers = ref(false)
const submitting = ref(false)

const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

const eventTypeOptions = [
  { value: 'SINGLE', label: t('addEvent.typeSingle') },
  { value: 'CYCLE', label: t('addEvent.typeCycle') }
]

const categoryTypeOptions = [
  { value: 'training', label: t('addEvent.categoryTraining') },
  { value: 'match', label: t('addEvent.categoryMatch') },
  { value: 'other', label: t('addEvent.categoryOther') }
]

const cyclePeriodOptions = [
  { value: 'DAY', label: t('addEvent.cycleDaily') },
  { value: 'ONE_WEEK', label: t('addEvent.cycleWeekly') },
  { value: 'TWO_WEEK', label: t('addEvent.cycleBiweekly') },
  { value: 'MONTHLY', label: t('addEvent.cycleMonthly') },
  { value: 'YEAR', label: t('addEvent.cycleYearly') }
]

const showCycleFields = computed(() => eventType.value === 'CYCLE')

const fetchCategories = async () => {
  if (!clubUuid.value) return
  loadingCategories.value = true
  try {
    const response = await categoryService.getCategories(clubUuid.value, 1, 9999)
    categories.value = response.data || []
  } catch (err: any) {
    toastStore.error(t('addEvent.errorFetchingCategories'))
  } finally {
    loadingCategories.value = false
  }
}

const fetchCoaches = async () => {
  if (!clubUuid.value) return
  loadingCoaches.value = true
  try {
    const response = await coachService.getCoaches(clubUuid.value, 9999)
    coaches.value = response.data || []
  } catch (err: any) {
    toastStore.error(t('addEvent.errorFetchingCoaches'))
  } finally {
    loadingCoaches.value = false
  }
}

const fetchPlayers = async () => {
  if (!clubUuid.value) return
  loadingPlayers.value = true
  try {
    const response = await playerService.getPlayers(clubUuid.value, { per_page: 9999 })
    players.value = response.data?.players || []
  } catch (err: any) {
    console.error('Error fetching players:', err)
  } finally {
    loadingPlayers.value = false
  }
}

const handleSubmit = async () => {
  if (!clubUuid.value) {
    toastStore.error(t('addEvent.errorNoClub'))
    return
  }

  if (!title.value || !eventType.value || !categoryType.value || !startDate.value) {
    toastStore.error(t('addEvent.errorFillRequired'))
    return
  }

  if (eventType.value === 'CYCLE' && !cyclePeriod.value) {
    toastStore.error(t('addEvent.errorFillRequired'))
    return
  }

  submitting.value = true

  try {
    const data: any = {
      title: title.value,
      type: eventType.value,
      category_type: categoryType.value,
      start_date: startDate.value,
      start_time: startTime.value,
      end_time: endTime.value
    }

    if (place.value) data.place = place.value
    if (endDate.value) data.end_date = endDate.value

    if (eventType.value === 'CYCLE') {
      data.cycle_period = cyclePeriod.value
      if (cycleEndDate.value) data.cycle_end_date = cycleEndDate.value
    }

    if (selectedCoaches.value.length > 0) data.coaches = selectedCoaches.value
    if (selectedPlayers.value.length > 0) data.players = selectedPlayers.value

    const response = await eventService.createEvent(clubUuid.value, data)
    toastStore.success(response?.message || t('addEvent.eventCreated'))
    router.push('/events')
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || t('addEvent.errorCreatingEvent')
    toastStore.error(errorMessage)
  } finally {
    submitting.value = false
  }
}

watch(startDate, (newVal) => {
  if (newVal && !endDate.value) endDate.value = newVal
})

watch(startTime, (newVal) => {
  if (newVal && !endTime.value) {
    const [hours, minutes] = newVal.split(':').map(Number)
    const total = hours * 60 + minutes + 90
    endTime.value = `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
  }
})

onMounted(() => {
  fetchCategories()
  fetchCoaches()
  fetchPlayers()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link to="/events" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('addEvent.backToEvents') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('addEvent.title') }} <span class="text-sm font-normal text-gray-500">v2</span></h1>
      </div>

      <!-- Form -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="handleSubmit">
          <!-- Basic Fields -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('addEvent.basicInfo') }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Event Name -->
              <div class="md:col-span-2">
                <label for="eventName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('addEvent.eventName') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="eventName"
                  v-model="title"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Event Type -->
              <div>
                <label for="eventType" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('addEvent.eventType') }} <span class="text-red-600">*</span>
                </label>
                <select
                  id="eventType"
                  v-model="eventType"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                >
                  <option value="">{{ t('addEvent.selectType') }}</option>
                  <option v-for="option in eventTypeOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- Category Type -->
              <div>
                <label for="categoryType" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('addEvent.categoryType') }} <span class="text-red-600">*</span>
                </label>
                <select
                  id="categoryType"
                  v-model="categoryType"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                >
                  <option value="">{{ t('addEvent.selectCategory') }}</option>
                  <option v-for="option in categoryTypeOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- Recurring Fields -->
              <div v-if="showCycleFields" class="md:col-span-2">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label for="cyclePeriod" class="block text-sm font-medium text-gray-700 mb-1">
                      {{ t('addEvent.cyclePeriod') }} <span class="text-red-600">*</span>
                    </label>
                    <select
                      id="cyclePeriod"
                      v-model="cyclePeriod"
                      :required="showCycleFields"
                      class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                    >
                      <option value="">{{ t('addEvent.selectCyclePeriod') }}</option>
                      <option v-for="option in cyclePeriodOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label for="cycleEndDate" class="block text-sm font-medium text-gray-700 mb-1">
                      {{ t('addEvent.cycleEndDate') }}
                    </label>
                    <input
                      type="date"
                      id="cycleEndDate"
                      v-model="cycleEndDate"
                      class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                    >
                  </div>
                </div>
              </div>

              <!-- Start Date -->
              <div>
                <label for="startDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('addEvent.startDate') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  id="startDate"
                  v-model="startDate"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Start Time -->
              <div>
                <label for="startTime" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('addEvent.startTime') }} <span class="text-red-600">{{ t('common.required') }}</span>
                </label>
                <input
                  type="time"
                  id="startTime"
                  v-model="startTime"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- End Date -->
              <div>
                <label for="endDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('addEvent.endDate') }}
                </label>
                <input
                  type="date"
                  id="endDate"
                  v-model="endDate"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- End Time -->
              <div>
                <label for="endTime" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('addEvent.endTime') }} <span class="text-red-600">{{ t('common.required') }}</span>
                </label>
                <input
                  type="time"
                  id="endTime"
                  v-model="endTime"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Place -->
              <div class="md:col-span-2">
                <label for="place" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('addEvent.place') }}
                </label>
                <input
                  type="text"
                  id="place"
                  v-model="place"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>
            </div>
          </div>

          <!-- Participant Selection V2 -->
          <div class="mb-6">
            <div v-if="loadingCategories || loadingCoaches || loadingPlayers" class="flex items-center gap-2 py-2 text-sm text-gray-500">
              <div class="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              Ładowanie uczestników...
            </div>
            <ParticipantSelectorV2
              v-else
              :categories="categories"
              :players="players"
              :coaches="coaches"
              v-model:selectedPlayers="selectedPlayers"
              v-model:selectedCoaches="selectedCoaches"
            />
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end">
            <button
              type="submit"
              :disabled="submitting"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ submitting ? t('addEvent.adding') : t('addEvent.addEvent') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
